import { PLACE_ID_PATTERN } from '@bandido/shared/types';
import type { PlaceCoords } from '@bandido/shared/types';

import { prettifyCode } from './format';
import {
  PricedSearchResult,
  RailSearchPayload,
} from './queries';
import { PlaceRef } from './places';
import { routeMapEndpoints } from './routeMap';
import type { RouteMapPoint } from './routeMap';

/** Tramo de un viaje en tren (un tren concreto; un journey puede tener conexiones). */
export interface RailSegment {
  fromStation: string;
  toStation: string;
  departure: string;
  arrival: string;
  carrier: string;
  transport: string;
  identifier?: string;
  /** Logo del operador del tramo (ya validado por el backend). */
  carrierLogoUrl?: string;
}

/**
 * Condición de la tarifa tal como la publica el OPERADOR (cambios, devolución):
 * el texto viene en inglés y no se traduce — es la letra chica que se le
 * transmite al pasajero.
 */
export interface RailFareCondition {
  type: string;
  description: string;
}

/** Oferta de tren: es una TARIFA (clase + flexibilidad) de un journey concreto. */
export type RailOffer = PricedSearchResult & {
  carrier?: string;
  carrierLogoUrl?: string;
  fromStation?: string;
  toStation?: string;
  departure?: string;
  arrival?: string;
  segments?: RailSegment[];
  flexibility?: string;
  serviceClass?: string;
  fareClass?: string;
  fareConditions?: RailFareCondition[];
};

/**
 * Un viaje (tren) con todas sus tarifas. El hub devuelve una oferta POR
 * COMBINACIÓN de clase/flexibilidad, así que se agrupan por journey para que
 * el agente elija primero el tren y después la tarifa.
 */
export interface RailJourney {
  key: string;
  carrier: string;
  /** Logo del operador, si el proveedor lo publica. */
  carrierLogoUrl?: string;
  fromStation: string;
  toStation: string;
  departure: string;
  arrival: string;
  segments: RailSegment[];
  /** Tarifas del journey, de la más barata a la más cara (una por etiqueta). */
  fares: RailOffer[];
  /**
   * Variantes extra que el proveedor publica con la MISMA etiqueta de tarifa y
   * otro precio (pasa con All Aboard): se muestra la más barata y se informa
   * cuántas quedaron detrás, en vez de repetir filas idénticas.
   */
  extraVariants: number;
}

/** "2026-08-19T08:27" → "08:27" (hora LOCAL de la estación, sin offset). */
export function formatLocalTime(value?: string): string {
  if (!value) return '—';
  const time = value.split('T')[1] ?? '';
  return time.slice(0, 5) || '—';
}

export function formatLocalDate(value?: string): string {
  if (!value) return '';
  const [date] = value.split('T');
  const parts = date?.split('-');
  return parts?.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : '';
}

/**
 * Marca de tiempo ISO tal como la publica el hub, con o sin segundos, fracción
 * u offset. Se valida la FORMA antes de parsear porque `new Date` es mucho más
 * laxo que el contrato: `new Date('sin hora:00')` no falla, cae en su parser
 * de rescate y devuelve el año 2000 — una duración de 234.441 h en la card en
 * vez de un "—" honesto.
 */
const HUB_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

/** Duración entre dos horas locales del mismo journey ("3h 17m"). */
export function formatDuration(departure?: string, arrival?: string): string {
  if (!departure || !arrival) return '—';
  if (!HUB_DATE_TIME.test(departure) || !HUB_DATE_TIME.test(arrival)) return '—';
  const from = new Date(`${departure}:00`.slice(0, 19));
  const to = new Date(`${arrival}:00`.slice(0, 19));
  const minutes = Math.round((to.getTime() - from.getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 0) return '—';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours}h ${rest}m` : `${rest}m`;
}

/** Una combinación del viaje: dónde se cambia de tren y cuánto se espera. */
export interface RailConnection {
  station: string;
  /** Espera entre la llegada de un tramo y la salida del siguiente ("35m"). */
  wait: string;
}

/**
 * Las combinaciones del viaje. La ESPERA es lo que decide si el viaje es
 * viable —veinte minutos en una estación grande no alcanzan— así que viaja
 * pegada a la estación: es el dato que se perdería al resumir el recorrido en
 * "1 combinación".
 *
 * Un viaje sin tramos publicados (o de un solo tramo) no tiene combinaciones:
 * es directo, y el arquetipo lo dice con un flag en vez de una lista vacía.
 */
export function railConnections(journey: RailJourney): RailConnection[] {
  return journey.segments.slice(1).map((segment, index) => ({
    station: segment.fromStation,
    wait: formatDuration(journey.segments[index].arrival, segment.departure),
  }));
}

/** Agrupa las tarifas por viaje y las ordena por precio; los viajes, por salida. */
export function groupRailJourneys(results: PricedSearchResult[]): RailJourney[] {
  const journeys = new Map<string, RailJourney>();

  for (const result of results as RailOffer[]) {
    const segments = Array.isArray(result.segments) ? result.segments : [];
    // Identidad del viaje: salida/llegada + operador + los trenes del recorrido
    // (dos tarifas del mismo tren comparten todo esto).
    const key = [
      result.departure ?? '',
      result.arrival ?? '',
      result.carrier ?? '',
      segments.map((segment) => segment.identifier ?? segment.carrier).join('>'),
    ].join('|');

    const existing = journeys.get(key);
    if (existing) {
      existing.fares.push(result);
      continue;
    }
    journeys.set(key, {
      key,
      carrier: result.carrier ?? result.provider,
      carrierLogoUrl: result.carrierLogoUrl ?? segments[0]?.carrierLogoUrl,
      fromStation: result.fromStation ?? '',
      toStation: result.toStation ?? '',
      departure: result.departure ?? '',
      arrival: result.arrival ?? '',
      segments,
      fares: [result],
      extraVariants: 0,
    });
  }

  const sorted = [...journeys.values()];
  for (const journey of sorted) {
    const byLabel = new Map<string, RailOffer>();
    let extra = 0;
    for (const fare of journey.fares) {
      const label = fareLabel(fare);
      const current = byLabel.get(label);
      if (!current) {
        byLabel.set(label, fare);
        continue;
      }
      extra++;
      // Se conserva la más barata de cada etiqueta (es la que se vendería).
      if (fare.pricing.final.amountMinor < current.pricing.final.amountMinor) {
        byLabel.set(label, fare);
      }
    }
    journey.fares = [...byLabel.values()].sort(
      (a, b) => a.pricing.final.amountMinor - b.pricing.final.amountMinor,
    );
    journey.extraVariants = extra;
  }
  return sorted.sort((a, b) => a.departure.localeCompare(b.departure));
}

/**
 * Por dónde se compra el viaje, pero SOLO si todas sus tarifas se compran por
 * el mismo lado. `groupRailJourneys` agrupa por tren —salida, llegada,
 * operador y trenes del recorrido— y el proveedor NO entra en esa identidad:
 * dos conectores que vendan el mismo AVE caen en el mismo viaje.
 *
 * Por eso atribuir el viaje entero al proveedor de la tarifa más barata
 * mentiría: el asesor leería "vía A" y la tarifa que elige se reservaría por
 * B. `null` significa que hay más de uno y que la atribución tiene que bajar a
 * cada tarifa, que es lo que efectivamente se reserva.
 */
export function railJourneyProvider(journey: RailJourney): string | null {
  const providers = new Set(journey.fares.map((fare) => fare.provider));
  return providers.size === 1 ? [...providers][0] : null;
}

/** Etiqueta legible de la tarifa: usa lo que publique el proveedor. */
export function fareLabel(fare: RailOffer): string {
  const parts = [fare.serviceClass, fare.flexibility]
    .filter(Boolean)
    .map((part) => prettifyCode(part as string));
  if (parts.length > 0) return parts.join(' · ');
  return fare.fareClass ? prettifyCode(fare.fareClass) : '—';
}

/**
 * Lugar (`plc_*`) reconstruido desde la URL. Una referencia anterior o ajena
 * al contrato canónico no se muestra ni se ejecuta.
 *
 * `kind`/`breadcrumb` quedan con el `name` como placeholder: ninguno de los
 * dos se renderiza para el valor YA seleccionado (el ícono y la miga de
 * `renderOption` solo corren sobre las OPCIONES del dropdown, ver
 * `PlaceAutocomplete.tsx`), así que no vale la pena otro par de parámetros de
 * URL para algo que nunca se ve.
 */
export function railPlaceFromParams(
  id: string | undefined,
  name: string | undefined,
): PlaceRef | null {
  if (!id || !PLACE_ID_PATTERN.test(id)) return null;
  const resolvedName = name ?? id;
  return { id, name: resolvedName, breadcrumb: resolvedName, kind: 'city' };
}

/**
 * Los params canónicos de ruta. Función pura (no
 * vive en el `.tsx`) para poder testear el caso que importa: **`url.commit`
 * MERGEA sobre los params actuales** (`useSearchUrlState.ts`), así que el
 * Las claves `origenUid`/`destinoUid` se borran explícitamente para limpiar
 * URLs antiguas sin interpretar esos valores.
 *
 * `undefined` es la forma de borrar: `cleanParams` descarta los vacíos.
 */
export function railRouteParams(
  _placesEnabled: boolean,
  from: { id: string; name: string },
  to: { id: string; name: string },
): Record<string, string | undefined> {
  return {
    origen: from.id,
    origenNombre: from.name,
    origenUid: undefined,
    destino: to.id,
    destinoNombre: to.name,
    destinoUid: undefined,
  };
}

/**
 * Un extremo del tren pedido: su nombre para mostrar y sus coordenadas.
 *
 * La referencia comiteada es siempre `plc_*` y se resuelve contra Places.
 */
export interface RailStationEndpointSources {
  /** Coordenadas ya resueltas del `plc_*` comiteado, indexadas por id. */
  placeCoords?: Record<string, PlaceCoords>;
  /**
   * Nombre para mostrar; la referencia canónica viaja aparte en la URL.
   */
  label?: string;
}

function railStationEndpoint(
  committed: string,
  sources: RailStationEndpointSources,
): { label: string; coords?: PlaceCoords } {
  return {
    label: sources.label ?? committed,
    coords: sources.placeCoords?.[committed],
  };
}

/**
 * Origen y destino del tren pedido, para el mapa del recorrido (#2228,
 * ampliado por #2247 al modo lugares y a las búsquedas reconstruidas).
 *
 * Sale del PEDIDO, no del formulario vivo: si el asesor cambió la estación
 * después de buscar, el mapa sigue siendo el de la búsqueda que produjo la
 * oferta — o no hay mapa, nunca el de otra ruta.
 */
export function railRouteMapPoints(
  payload: RailSearchPayload | null,
  from: RailStationEndpointSources,
  to: RailStationEndpointSources,
): RouteMapPoint[] {
  if (!payload) return [];
  return routeMapEndpoints(
    railStationEndpoint(payload.fromStation, from),
    railStationEndpoint(payload.toStation, to),
  );
}
